import { useState, useEffect } from 'react';
import { Card, Table, Button, Alert, Spinner, Badge, Modal } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { settings } from '../config/settings';
import { formatClaimValue, UserStatus } from '../utils/jwtUtils';
import { apiFetch } from '../utils/apiClient';

const SYNC_USER_ENDPOINT = settings?.api?.baseUrl && settings?.api?.users?.sync?.apiEndpoint 
    ? `${settings.api.baseUrl}${settings.api.users.sync.apiEndpoint}` 
    : null;

export const Profile = () => {
    const { user, getIdToken, getClaims } = useAuth();
    const [syncing, setSyncing] = useState(false);
    const [syncError, setSyncError] = useState(null);
    const [syncSuccess, setSyncSuccess] = useState(null);
    const [claims, setClaims] = useState(null);
    const [loadingClaims, setLoadingClaims] = useState(false);
    const [showStatusModal, setShowStatusModal] = useState(false);

    // Load claims when component mounts or user changes
    useEffect(() => {
        const loadClaims = async () => {
            if (!user) {
                setClaims(null);
                return;
            }

            setLoadingClaims(true);
            try {
                const token = await getIdToken(false);
                if (token) {
                    const decodedClaims = await getClaims(false);
                    setClaims(decodedClaims);
                }
            } catch (error) {
                console.error('Error loading claims:', error);
            } finally {
                setLoadingClaims(false);
            }
        };

        loadClaims();
    }, [user, getIdToken, getClaims]);

    if (!user) {
        return null;
    }

    const metadata = user.metadata || {};

    const rows = [
        { label: 'Nombre', value: user.displayName || 'No disponible' },
        { label: 'Email', value: user.email || 'No disponible' },
        { label: 'UID', value: user.uid },
        { label: 'Proveedor', value: user.providerData?.[0]?.providerId || 'google.com' },
        { label: 'Creado', value: metadata.creationTime || 'No disponible' },
        { label: 'Último acceso', value: metadata.lastSignInTime || 'No disponible' },
        { label: 'Email verificado', value: user.emailVerified ? 'Sí' : 'No' },
    ];

    const handleSyncUser = async () => {
        if (!SYNC_USER_ENDPOINT) {
            setSyncError('El endpoint de sincronización no está configurado.');
            return;
        }

        setSyncing(true);
        setSyncError(null);
        setSyncSuccess(null);

        try {
            const token = await getIdToken();
            if (!token) {
                throw new Error('No fue posible obtener el token de autenticación.');
            }

            const response = await apiFetch(SYNC_USER_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                const errorText = await response.text();
                let errorMessage = `Error al sincronizar usuario: ${response.status}`;
                
                try {
                    const errorJson = JSON.parse(errorText);
                    errorMessage = errorJson.message || errorJson.details || errorMessage;
                } catch {
                    errorMessage = errorText || errorMessage;
                }
                
                throw new Error(errorMessage);
            }

            const data = await response.json();
            setSyncSuccess(data.message || 'Usuario sincronizado correctamente en el backend.');

            // Refresh token and get updated claims from backend
            // getClaims(true) will internally call getIdToken(true) which refreshes the token
            // So we only need one call instead of three separate refresh calls
            try {
                const updatedClaims = await getClaims(true);
                if (updatedClaims) {
                    setClaims(updatedClaims);
                    
                    // Check if user status has changed and show warning if not active
                    const statusValue = typeof updatedClaims.status === 'string' 
                        ? updatedClaims.status 
                        : (typeof updatedClaims.status === 'number' ? updatedClaims.status : null);
                    
                    if (statusValue !== null && statusValue !== 'Active' && statusValue !== UserStatus.Active) {
                        setShowStatusModal(true);
                    }
                }
            } catch (refreshError) {
                console.error('Error refreshing token:', refreshError);
                // Don't show error to user, just log it
            }
        } catch (err) {
            console.error('Error al sincronizar usuario:', err);
            setSyncError(err.message || 'Ocurrió un error al sincronizar el usuario.');
        } finally {
            setSyncing(false);
        }
    };

    // Extract custom claims (filter out standard Firebase claims)
    const standardClaims = [
        'iss', 'aud', 'auth_time', 'sub', 'iat', 'exp', 
        'firebase', 'email', 'email_verified', 'name', 
        'picture', 'user_id'
    ];
    
    const customClaims = claims ? Object.entries(claims)
        .filter(([key]) => !standardClaims.includes(key))
        .reduce((acc, [key, value]) => {
            acc[key] = value;
            return acc;
        }, {}) : {};

    return (
        <>
            <Card className="mt-4">
            <Card.Header>
                <Card.Title>Tu perfil</Card.Title>
                <Card.Subtitle className="text-muted">Información obtenida desde Firebase Authentication.</Card.Subtitle>
            </Card.Header>
            <Card.Body>
                <div className="mb-3">
                    <Button
                        variant="primary"
                        onClick={handleSyncUser}
                        disabled={syncing}
                    >
                        {syncing ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                Sincronizando...
                            </>
                        ) : (
                            'Sincronizar con Backend'
                        )}
                    </Button>
                    <p className="text-muted small mt-2 mb-0">
                        Sincroniza tu usuario de Firebase con la base de datos del backend.
                    </p>
                </div>

                {syncSuccess && (
                    <Alert variant="success" dismissible onClose={() => setSyncSuccess(null)}>
                        <Alert.Heading>¡Éxito!</Alert.Heading>
                        <p>{syncSuccess}</p>
                    </Alert>
                )}

                {syncError && (
                    <Alert variant="danger" dismissible onClose={() => setSyncError(null)}>
                        <Alert.Heading>Error</Alert.Heading>
                        <p>{syncError}</p>
                    </Alert>
                )}

                <Table responsive striped hover>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.label}>
                                <td><strong>{row.label}</strong></td>
                                <td>{row.value}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Card.Body>
        </Card>

        {/* Custom Claims Card */}
        <Card className="mt-4">
            <Card.Header>
                <Card.Title>Custom Claims del Token</Card.Title>
                <Card.Subtitle className="text-muted">
                    Claims personalizados obtenidos del token JWT de Firebase.
                </Card.Subtitle>
            </Card.Header>
            <Card.Body>
                {loadingClaims ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Cargando claims...</span>
                        </Spinner>
                    </div>
                ) : claims ? (
                    <>
                        {Object.keys(customClaims).length === 0 ? (
                            <Alert variant="info">
                                <Alert.Heading>Sin Custom Claims</Alert.Heading>
                                <p className="mb-0">
                                    No hay custom claims en el token. 
                                    Sincroniza tu usuario con el backend para añadir claims (roles, status, etc.).
                                </p>
                            </Alert>
                        ) : (
                            <>
                                <p className="text-muted small mb-3">
                                    Estos claims fueron añadidos por el backend después de sincronizar el usuario.
                                </p>
                                <Table responsive striped hover size="sm">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '30%' }}>Claim</th>
                                            <th style={{ width: '70%' }}>Valor</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Object.entries(customClaims)
                                            .sort(([a], [b]) => a.localeCompare(b))
                                            .map(([key, value]) => {

                                                const displayValue = formatClaimValue(value, key);
                                                const isArray = Array.isArray(value);
                                                const isObject = typeof value === 'object' && value !== null && !isArray;

                                                return (
                                                    <tr key={key}>
                                                        <td>
                                                            <strong>{key}</strong>
                                                            {isArray && (
                                                                <Badge bg="info" className="ms-2">
                                                                    Array
                                                                </Badge>
                                                            )}
                                                            {isObject && (
                                                                <Badge bg="secondary" className="ms-2">
                                                                    Object
                                                                </Badge>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {isArray ? (
                                                                <div>
                                                                    {value.map((item, idx) => (
                                                                        <Badge key={idx} bg="primary" className="me-1">
                                                                            {item}
                                                                        </Badge>
                                                                    ))}
                                                                </div>
                                                            ) : isObject ? (
                                                                <pre className="mb-0" style={{ fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>
                                                                    {displayValue}
                                                                </pre>
                                                            ) : (
                                                                <code style={{ fontSize: '0.9rem' }}>
                                                                    {displayValue}
                                                                </code>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                    </tbody>
                                </Table>
                            </>
                        )}
                    </>
                ) : (
                    <Alert variant="warning">
                        <Alert.Heading>No se pudieron cargar los claims</Alert.Heading>
                        <p className="mb-0">
                            No fue posible decodificar el token para obtener los claims.
                        </p>
                    </Alert>
                )}
            </Card.Body>
        </Card>

        {/* Status Warning Modal */}
        <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)} centered>
            <Modal.Header closeButton>
                <Modal.Title>Usuario no activo</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Alert variant="warning" className="mb-0">
                    El usuario no está activo. Consulte a su administrador.
                </Alert>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="primary" onClick={() => setShowStatusModal(false)}>
                    Entendido
                </Button>
            </Modal.Footer>
        </Modal>
        </>
    );
};